import { PermissionsTypes } from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { WsRequest } from '@gem/call/WsApi/unauthorizedWsCallsApi/requests/request.types';
import {
  CallPermission_AccessType,
  CallPermission_RequestStatus,
  CallPermissions,
  CallPermissions_Level,
  MemberWithCustomPermissions,
  PersonalizedCallPermissions,
  PersonalizedCallPermissions_Level,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/call/call_model_pb';
import {
  RealtimeCallEvent_MemberPermissionRequestChanged,
  RealtimeCallEvent_MemberPermissionRequestCreated,
  RealtimeCallEvent_MemberPermissionsChanged,
  RealtimeCallEvent_PermissionsChanged,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/call/call_realtime_events_pb';
import {
  GetCallPermissionsSuccess,
  GetPermissionRequestsSuccess,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/call/call_responses_pb';

import {
  ICallCustomPermissions,
  ICallPermissionRequests,
  ICallPermissions,
  ICallPermissionsChanged,
  ICallPermissionsSettings,
  ICallPersonalizedPermissions,
  ICallUserPermissionRequestCreated,
  ICallUserPermissionsChanged,
  ICallUserPermissionsRequestChanged,
  PermissionRequestAccess,
  PermissionsPersonalizedValue,
  PermissionsRequestStatuses,
  PermissionsValue,
} from '../callPermissionsModel.types';

const callUnauthorizedPermissionsLevelResponseMap: Record<CallPermissions_Level, PermissionsValue> =
  {
    [CallPermissions_Level.EVERYONE]: PermissionsValue.Everyone,
    [CallPermissions_Level.AFTER_APPROVE]: PermissionsValue.AfterApprove,
    [CallPermissions_Level.ADMINS_AND_OWNER]: PermissionsValue.AdminsAndOwner,
  };

export const mapUnauthorizedDefaultPermissionsResponse = (
  permissions: CallPermissions
): ICallPermissions => {
  return {
    switchOnCamera: callUnauthorizedPermissionsLevelResponseMap[permissions.switchOnCamera],
    switchOnMic: callUnauthorizedPermissionsLevelResponseMap[permissions.switchOnMic],
    shareScreen: callUnauthorizedPermissionsLevelResponseMap[permissions.shareScreen],
  };
};

const callUnauthorizedPermissionsPersonalizedLevelResponseMap: Record<
  PersonalizedCallPermissions_Level,
  PermissionsPersonalizedValue
> = {
  [PersonalizedCallPermissions_Level.ALWAYS]: PermissionsPersonalizedValue.Always,
  [PersonalizedCallPermissions_Level.AFTER_APPROVE]: PermissionsPersonalizedValue.AfterApprove,
  [PersonalizedCallPermissions_Level.NEVER]: PermissionsPersonalizedValue.Never,
};

export const mapUnauthorizedPersonalizedPermissionsResponse = (
  permissions: PersonalizedCallPermissions
): ICallPersonalizedPermissions => {
  const personalizedPermissions = {} as ICallPersonalizedPermissions;
  if (typeof permissions.switchOnCamera === 'number') {
    personalizedPermissions[PermissionsTypes.SwitchOnCamera] =
      callUnauthorizedPermissionsPersonalizedLevelResponseMap[permissions.switchOnCamera];
  }

  if (typeof permissions.switchOnMic === 'number') {
    personalizedPermissions[PermissionsTypes.SwitchOnMic] =
      callUnauthorizedPermissionsPersonalizedLevelResponseMap[permissions.switchOnMic];
  }

  if (typeof permissions.shareScreen === 'number') {
    personalizedPermissions[PermissionsTypes.ShareScreen] =
      callUnauthorizedPermissionsPersonalizedLevelResponseMap[permissions.shareScreen];
  }

  return personalizedPermissions;
};

export const mapUnauthorizedUsersWithCustomPermissionsResponse = (
  permissions: MemberWithCustomPermissions[]
): ICallCustomPermissions[] => {
  return permissions.map((item) => ({
    userId: String(item.memberId),
    personalizedCallPermissions: mapUnauthorizedPersonalizedPermissionsResponse(item.personalized!),
  }));
};

export const mapCallUnauthorizedPermissionsResponse = (
  permissions: GetCallPermissionsSuccess
): ICallPermissionsSettings => {
  const mappedPermissions: ICallPermissionsSettings = {
    default: mapUnauthorizedDefaultPermissionsResponse(permissions.default!),
    personalized: mapUnauthorizedPersonalizedPermissionsResponse(permissions.personalized!),
    defaultPersonalized: mapUnauthorizedPersonalizedPermissionsResponse(
      permissions.defaultPersonalized!
    ),
  };

  if ('membersWithCustomPermissions' in permissions) {
    mappedPermissions.usersWithCustomPermissions =
      mapUnauthorizedUsersWithCustomPermissionsResponse(permissions.membersWithCustomPermissions);
  }

  return mappedPermissions;
};

export const mapCallUnauthorizedPermissionsRequestsResponse = (
  permissionsRequests: GetPermissionRequestsSuccess
): ICallPermissionRequests => {
  return {
    switchOnCamera: permissionsRequests.switchOnCamera.map((item) => String(item)),
    switchOnMic: permissionsRequests.switchOnMic.map((item) => String(item)),
    shareScreen: permissionsRequests.shareScreen.map((item) => String(item)),
  };
};

const callUnauthorizedPermissionsLevelRequestMap: Record<PermissionsValue, CallPermissions_Level> =
  {
    [PermissionsValue.Everyone]: CallPermissions_Level.EVERYONE,
    [PermissionsValue.AdminsAndOwner]: CallPermissions_Level.ADMINS_AND_OWNER,
    [PermissionsValue.AfterApprove]: CallPermissions_Level.AFTER_APPROVE,
  };

export const mapCallUnauthorizedDefaultPermissionsRequest = (
  permissions: Required<ICallPermissions>
): WsRequest<CallPermissions> => {
  return {
    switchOnCamera:
      callUnauthorizedPermissionsLevelRequestMap[permissions[PermissionsTypes.SwitchOnCamera]],
    switchOnMic:
      callUnauthorizedPermissionsLevelRequestMap[permissions[PermissionsTypes.SwitchOnMic]],
    shareScreen:
      callUnauthorizedPermissionsLevelRequestMap[permissions[PermissionsTypes.ShareScreen]],
  };
};

const callUnauthorizedPermissionsPersonalizedLevelRequestMap: Record<
  PermissionsPersonalizedValue,
  PersonalizedCallPermissions_Level
> = {
  [PermissionsPersonalizedValue.AfterApprove]: PersonalizedCallPermissions_Level.AFTER_APPROVE,
  [PermissionsPersonalizedValue.Never]: PersonalizedCallPermissions_Level.NEVER,
  [PermissionsPersonalizedValue.Always]: PersonalizedCallPermissions_Level.ALWAYS,
};

export const mapUnauthorizedPersonalizedPermissionsRequest = (
  permissions: ICallPersonalizedPermissions
): PersonalizedCallPermissions => {
  const personalizedPermissions = {} as PersonalizedCallPermissions;
  if (permissions[PermissionsTypes.SwitchOnCamera]) {
    personalizedPermissions.switchOnCamera =
      callUnauthorizedPermissionsPersonalizedLevelRequestMap[
        permissions[PermissionsTypes.SwitchOnCamera]
      ];
  }

  if (permissions[PermissionsTypes.SwitchOnMic]) {
    personalizedPermissions.switchOnMic =
      callUnauthorizedPermissionsPersonalizedLevelRequestMap[
        permissions[PermissionsTypes.SwitchOnMic]
      ];
  }

  if (permissions[PermissionsTypes.ShareScreen]) {
    personalizedPermissions.shareScreen =
      callUnauthorizedPermissionsPersonalizedLevelRequestMap[
        permissions[PermissionsTypes.ShareScreen]
      ];
  }

  return personalizedPermissions;
};

export const callUnauthorizedPermissionsRequestStatusRequestMap: Record<
  PermissionsRequestStatuses,
  CallPermission_RequestStatus
> = {
  [PermissionsRequestStatuses.Approved]: CallPermission_RequestStatus.APPROVED,
  [PermissionsRequestStatuses.Rejected]: CallPermission_RequestStatus.DECLINED,
};

export const callUnauthorizedPermissionsRequestStatusResponseMap: Record<
  CallPermission_RequestStatus,
  PermissionsRequestStatuses
> = {
  [CallPermission_RequestStatus.APPROVED]: PermissionsRequestStatuses.Approved,
  [CallPermission_RequestStatus.DECLINED]: PermissionsRequestStatuses.Rejected,
};

export const callUnauthorizedPermissionAccessRequestMap: Record<
  PermissionRequestAccess,
  CallPermission_AccessType
> = {
  [PermissionRequestAccess.Mic]: CallPermission_AccessType.MIC,
  [PermissionRequestAccess.Camera]: CallPermission_AccessType.CAMERA,
  [PermissionRequestAccess.ScreenSharing]: CallPermission_AccessType.SCREEN_SHARING,
};

export const mapUnauthorizedPermissionsChangedEvent = (
  data: RealtimeCallEvent_PermissionsChanged
): ICallPermissionsChanged => {
  const changedPermissions = {
    [PermissionsTypes.ShareScreen]:
      typeof data.changed!.shareScreen === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.shareScreen]
        : undefined,
    [PermissionsTypes.SwitchOnMic]:
      typeof data.changed!.switchOnMic === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnMic]
        : undefined,
    [PermissionsTypes.SwitchOnCamera]:
      typeof data.changed!.switchOnCamera === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnCamera]
        : undefined,
  };

  return {
    changedPermissions,
    default: mapUnauthorizedDefaultPermissionsResponse(data.default!),
    defaultPersonalized: mapUnauthorizedPersonalizedPermissionsResponse(data.defaultPersonalized!),
  };
};

export const mapUnauthorizedPermissionsMemberChangedEvent = (
  data: RealtimeCallEvent_MemberPermissionsChanged
): ICallUserPermissionsChanged => {
  const personalizedCallPermissions = {
    [PermissionsTypes.ShareScreen]:
      typeof data.changed!.shareScreen === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.shareScreen]
        : undefined,
    [PermissionsTypes.SwitchOnMic]:
      typeof data.changed!.switchOnMic === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnMic]
        : undefined,
    [PermissionsTypes.SwitchOnCamera]:
      typeof data.changed!.switchOnCamera === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnCamera]
        : undefined,
  };
  return {
    personalizedCallPermissions,
    userId: data.memberId.toString(),
    purged: data.purged,
  };
};

export const callUnauthorizedUserPermissionAccessResponseMap: Record<
  CallPermission_AccessType,
  PermissionRequestAccess
> = {
  [CallPermission_AccessType.MIC]: PermissionRequestAccess.Mic,
  [CallPermission_AccessType.CAMERA]: PermissionRequestAccess.Camera,
  [CallPermission_AccessType.SCREEN_SHARING]: PermissionRequestAccess.ScreenSharing,
};

export const mapUnauthorizedPermissionsRequestCreatedEvent = (
  data: RealtimeCallEvent_MemberPermissionRequestCreated
): ICallUserPermissionRequestCreated => {
  return {
    userId: data.memberId.toString(),
    access: callUnauthorizedUserPermissionAccessResponseMap[data.access],
  };
};

export const mapUnauthorizedPermissionsRequestChangedEvent = (
  data: RealtimeCallEvent_MemberPermissionRequestChanged
): ICallUserPermissionsRequestChanged => {
  const personalizedCallPermissions = {
    [PermissionsTypes.ShareScreen]:
      typeof data.changed!.shareScreen === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.shareScreen]
        : undefined,
    [PermissionsTypes.SwitchOnMic]:
      typeof data.changed!.switchOnMic === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnMic]
        : undefined,
    [PermissionsTypes.SwitchOnCamera]:
      typeof data.changed!.switchOnCamera === 'number'
        ? callUnauthorizedPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnCamera]
        : undefined,
  };
  return {
    personalizedCallPermissions,
    userId: data.memberId.toString(),
    status: callUnauthorizedPermissionsRequestStatusResponseMap[data.status],
  };
};
